import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CalendarCheck, ChevronDown, Loader2, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import BookedMeetingForm from './BookedMeetingForm';
import BookedOnLinkPanel from './BookedOnLinkPanel';
import { useBookingGesture } from './prospect/useBookingGesture';
import { searchMeetingProspects, type MeetingProspect } from '@/api/endpoints/meetings';
import {
  preselectedMeetingProspect,
  prospectLabel,
  dedupeByProspect,
  customerForProspect,
  type CustomerCrmView,
} from '@/services/dialer/bookedMeeting';

/**
 * "Renseigner un rendez-vous pris" from the left navigation (Kevin,
 * 2026-09-23). The caller is not on a prospect card here, so this screen
 * adds the one thing the prospect card does not need: choosing who the
 * meeting is with.
 *
 * Kevin, 2026-10-07: the prospect is searched in every list of every client
 * of the caller, not only in the queue the dialer has loaded, with a client
 * selector on top. The search runs on the server (a list can hold thousands
 * of prospects); it answers the most recently called first, and the first
 * one a meeting can travel on is preselected.
 *
 * Everything after the prospect follows that prospect's client: with a CRM
 * that answers, the calendar (BookedMeetingForm, the same component the
 * prospect card uses); without one, the client's booking link.
 */

type CustomerChoice = CustomerCrmView & { company_name?: string | null; name?: string | null };

const ALL = 'all';
const SEARCH_DELAY_MS = 250;

interface BookedMeetingDialogProps {
  /** Le client regarde : choisi d'office, et repli quand le prospect n'en nomme aucun. */
  customer: CustomerChoice | null;
  /** Tous les clients de l'appelant. */
  customers?: CustomerChoice[];
  onComplete?: () => void;
}

const customerName = (c: CustomerChoice | null | undefined) => c?.company_name || c?.name || '';

export default function BookedMeetingDialog({ customer, customers = [], onComplete }: BookedMeetingDialogProps) {
  const { t } = useTranslation(['dialer', 'common']);

  const [clientId, setClientId] = useState<string>(customer?.id || ALL);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selected, setSelected] = useState<MeetingProspect | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // "All" is the caller's clients, never the whole organisation.
  const customerIds = useMemo(
    () => (clientId === ALL ? customers.map((c) => c.id).filter((id): id is string => !!id) : [clientId]),
    [clientId, customers],
  );

  const { data: found = [], isFetching } = useQuery({
    queryKey: ['meeting-prospects', customerIds, query],
    queryFn: () => searchMeetingProspects({ customerIds, query }),
    staleTime: 30_000,
  });
  const visible = useMemo(() => dedupeByProspect(found) as MeetingProspect[], [found]);

  // A new client starts a new choice; the unfiltered list preselects the
  // most recently called prospect. Typing a filter never moves the choice.
  useEffect(() => { setSelected(null); }, [clientId]);
  useEffect(() => {
    if (selected || query.trim()) return;
    setSelected(preselectedMeetingProspect(visible));
  }, [visible, query, selected]);

  // Le geste suit le client DU PROSPECT choisi, pas celui de la barre laterale.
  const clientDuProspect = useMemo(
    () => customerForProspect(selected, customers, customer),
    [selected, customers, customer],
  );
  const { pending: gesteEnAttente, gesture: geste } = useBookingGesture(
    clientDuProspect,
    selected?.prospect_phone ?? '',
  );
  const nameOfClient = (id: string) => customerName(customers.find((c) => c.id === id));

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center gap-2">
        <CalendarCheck className="w-5 h-5 text-indigo-600" />
        <h2 className="text-lg font-semibold text-foreground">{t('dialer:bookedMeeting.title')}</h2>
      </div>
      <p className="text-sm text-muted-foreground">{t('dialer:bookedMeeting.subtitle')}</p>

      {customers.length > 1 && (
        <label className="space-y-1.5 block">
          <span className="text-sm font-medium text-foreground/80">{t('dialer:bookedMeeting.clientLabel')}</span>
          <select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="w-full h-9 rounded-lg border border-border bg-card px-3 text-sm text-foreground"
            data-testid="rdv-client"
          >
            <option value={ALL}>{t('dialer:bookedMeeting.allClients')}</option>
            {customers.filter((c) => !!c.id).map((c) => (
              <option key={c.id as string} value={c.id as string}>{customerName(c) || c.id}</option>
            ))}
          </select>
        </label>
      )}

      {/* The choice stays closed: in nine cases out of ten the preselected
          prospect is the right one. It opens to change it and closes again. */}
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground/80">{t('dialer:bookedMeeting.prospectLabel')}</p>

        <button
          type="button"
          onClick={() => setPickerOpen((v) => !v)}
          aria-expanded={pickerOpen}
          className="w-full flex items-center justify-between gap-2 rounded-xl border border-border bg-card px-3 py-2.5 text-left transition-colors hover:bg-accent"
          data-testid="prospet-choisi"
        >
          <span className="min-w-0">
            <span className="block text-sm text-foreground truncate">
              {selected
                ? `${prospectLabel(selected)}${selected.prospect_company ? ` · ${selected.prospect_company}` : ''}`
                : t('dialer:bookedMeeting.noProspect')}
            </span>
            {selected?.source && (
              <span className="block text-xs text-muted-foreground truncate">{selected.source}</span>
            )}
          </span>
          <ChevronDown className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform ${pickerOpen ? 'rotate-180' : ''}`} />
        </button>

        {pickerOpen && (
          <>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('dialer:bookedMeeting.searchPlaceholder')}
                className="pl-9 pr-9"
                aria-label={t('dialer:bookedMeeting.searchPlaceholder')}
                autoFocus
              />
              {isFetching && (
                <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />
              )}
            </div>
            <div
              role="listbox"
              aria-label={t('dialer:bookedMeeting.prospectLabel')}
              className="border border-border rounded-xl max-h-56 overflow-y-auto vf-scroll-discreet divide-y divide-border/50 bg-card"
            >
              {!isFetching && visible.length === 0 && (
                <p className="px-3 py-4 text-sm text-muted-foreground">{t('dialer:bookedMeeting.noProspect')}</p>
              )}
              {visible.map((prospect) => {
                const isSelected = prospect.id === selected?.id;
                const details = [
                  prospect.source,
                  clientId === ALL ? nameOfClient(prospect.customer_id) : '',
                ].filter(Boolean).join(' · ');
                return (
                  <button
                    key={prospect.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => { setSelected(prospect); setSearch(''); setPickerOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                      isSelected ? 'bg-indigo-50 text-indigo-900 font-medium' : 'hover:bg-accent text-foreground/80'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="truncate">
                        {prospectLabel(prospect)}
                        {prospect.prospect_company && (
                          <span className="text-muted-foreground"> · {prospect.prospect_company}</span>
                        )}
                      </span>
                      {!prospect.has_finished_call && (
                        <span className="ml-auto shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                          {t('dialer:bookedMeeting.neverCalled')}
                        </span>
                      )}
                    </span>
                    {details && <span className="block text-xs text-muted-foreground truncate">{details}</span>}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {selected && !selected.has_finished_call && (
          <p className="text-xs text-amber-700">{t('dialer:bookedMeeting.neverCalledHint')}</p>
        )}
      </div>

      {/* Sans CRM branche, il n'y a pas de formulaire : le lien s'ouvre et le
          rendez-vous est marque comme obtenu. Avec un CRM, le calendrier. */}
      {gesteEnAttente ? (
        <div className="h-24 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : geste === 'link' ? (
        <BookedOnLinkPanel prospectId={selected?.id ?? null} customer={clientDuProspect} onDone={onComplete} />
      ) : (
        <BookedMeetingForm
          prospectId={selected?.id ?? null}
          customer={clientDuProspect}
          onDone={onComplete}
          onCancel={onComplete}
        />
      )}
    </div>
  );
}
